import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import InputRadio from 'components/inputs/input-radio/InputRadio';
import SVGIcon from 'components/svg-icon/SVGIcon';
import PieChart from 'components/graphs/echarts/pie-chart/PieChart';
import WordCloud from 'components/word-cloud/WordCloud';
import BarChartSimple from 'components/graphs/bar-chart-simple/BarChartSimple';
import Tooltip from 'components/Tooltip';
import Spinner from 'components/spinner/Spinner';

import StateStatisticsCard from './card/StateStatisticsCard';
import StateStatisticsCardItem from './card-item/StateStatisticsCardItem';
import StateStatisticsCardItemDivider from './divider/StateStatisticsCardItemDivider';
import StateStatisticsOverlap from './overlap/StateStatisticsOverlap';
import StateStatisticsCustomizableOverlap from './overlap-customizable/StateStatisticsCustomizableOverlap';
import StateStatisticsOverlapStatusChecker from './overlap-status-checker/StateStatisticsOverlapStatusChecker';

import { fetchStats, resetAggregates } from '../../reducers/statistics';

import { __t, applyTranslations } from 'utils/language';
import { colors } from 'utils/charts/utils';
import { getCountriesBarChartData } from 'utils/charts/bar-chart';
import { NUM_WORD_CLOUD_ITEMS, prepareWordCloudData } from 'utils/charts/word-cloud/word-cloud';
import {
  displayPieChart,
  getAgePieChartData,
  getFollowersPieChartData,
  getGenderPieChartData,
  preparePieChartData
} from 'utils/charts/pie-chart';

import type {
  ChartType,
  ListsStatsAggregateData,
  ListsStatsCountryType,
  ListsStatsDataType
} from 'types';

import './state-statistics.scss';

import type { ChangeEvent } from 'react';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./state-statistics.i18n.json'));

const availableChannels = ['instagram', 'tiktok'];

type StateProps = {
  aggregateData: ListsStatsAggregateData
};

type OwnProps = {
  campaignId: number,
  offerState: string,
  channel: string,
  onChannelChange: (a: 'instagram' | 'tiktok') => void
};

type Dispatchprops = {
  fetchStats: (channel?: string) => Promise<void>,
  dispatchResetAggregates: () => void
};

type Props = StateProps & OwnProps & Dispatchprops;

class StateStatistics extends React.PureComponent<Props> {
  componentDidMount() {
    this.fetchStats();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.campaignId !== prevProps.campaignId || this.props.offerState !== prevProps.offerState || this.props.channel !== prevProps.channel) {
      this.fetchStats();
    }
  }

  componentWillUnmount() {
    const { dispatchResetAggregates } = this.props;

    dispatchResetAggregates();
  }

  fetchStats = () => {
    this.props.fetchStats(this.props.channel);
  };

  handleChannelChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { channel, onChannelChange } = this.props;

    if (event.target.value === channel) {
      return;
    }

    onChannelChange(event.target.value === 'instagram' ? 'instagram' : 'tiktok');
  };

  renderChannelRadioButtons = () => {
    const { channel } = this.props;

    return (
      <div className="state-statistics__channel-selector">
        <div className="state-statistics__channel-selector-label">
          {__t('state_statistics.label_channell.name')}
        </div>
        {
          availableChannels.map(type => (
            <InputRadio
              key={`state-statistics--${type}`}
              name="channel"
              value={type}
              id={`state-statistics--${type}`}
              checked={channel === type}
              label={__t(`state_statistics.label_channel.${type}`)}
              onChange={this.handleChannelChange}
            />
          ))
        }
      </div>
    );
  };

  renderTopCountriesChart(
    chartType: ChartType,
    countryType: ListsStatsCountryType,
    colorType: string,
    audienceChart: boolean = false
  ): React.ReactNode {
    const { aggregateData } = this.props;
    const type = audienceChart ? 'percentage' : 'standard';
    const countries = getCountriesBarChartData(aggregateData[countryType], type, true);
    const titles = audienceChart ?
      [
        __t('state_statistics.label_country'),
        '',
        __t('state_statistics.label_followers'),
        __t('state_statistics.label_share')
      ] :
      [
        __t('state_statistics.label_country'),
        '',
        __t('state_statistics.label_total')
      ];

    return (
      <StateStatisticsCardItem
        key={`state-statistics-top-countries-${chartType}-${countryType}-${colorType}`}
        heading={__t(`state_statistics.${chartType}.top_countries.title`)}
        tooltipMessage={__t(`state_statistics.${chartType}.top_countries.tooltip`)}
      >
        <BarChartSimple
          items={countries}
          titles={titles}
          color={colors[colorType][1]}
          showPercentMarker={audienceChart}
        />
      </StateStatisticsCardItem>
    );
  }

  renderFollowersChart(): React.ReactNode {
    const { aggregateData } = this.props;
    const data = getFollowersPieChartData(aggregateData.tiers);
    const pieChartData = preparePieChartData(data, 'creator', { tooltipType: 'reversed' });

    return (
      <StateStatisticsCardItem
        key="state-statistics-creator-followers"
        heading={__t('state_statistics.creator.followers.title')}
        tooltipMessage={__t('state_statistics.creator.followers.tooltip')}
      >
        <PieChart
          id="creators-followers"
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </StateStatisticsCardItem>
    );
  }

  renderGenderChart(dataType: ListsStatsDataType, chartType: ChartType): React.ReactNode {
    const { aggregateData } = this.props;
    const data = getGenderPieChartData(aggregateData[dataType]);
    const pieChartData = preparePieChartData(data, chartType);

    return (
      <StateStatisticsCardItem
        key={`state-statistics-creators-gender-${chartType}`}
        heading={__t(`state_statistics.${chartType}.gender.title`)}
        tooltipMessage={__t(`state_statistics.${chartType}.gender.tooltip`)}
      >
        <PieChart
          id={`creators-gender-${chartType}`}
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </StateStatisticsCardItem>
    );
  }

  renderBrandAffinity(): React.ReactNode {
    const { aggregateData } = this.props;
    const wordCloudData = prepareWordCloudData(aggregateData.mostUsedBrandHashtags, NUM_WORD_CLOUD_ITEMS);

    return (
      <StateStatisticsCardItem
        key="state-statistics-word-cloud-brand-affinity"
        heading={__t('state_statistics.creator.brand_affinity.title')}
        tooltipMessage={__t('state_statistics.creator.brand_affinity.tooltip')}
      >
        <WordCloud
          id="word-cloud-brand-affinity"
          options={wordCloudData}
        />
      </StateStatisticsCardItem>
    );
  }

  renderAgeChart(): React.ReactNode {
    const { aggregateData } = this.props;
    const { audienceAgeSplit } = aggregateData;
    const data = getAgePieChartData(audienceAgeSplit);
    const pieChartData = preparePieChartData(data, 'audience');

    return (
      <StateStatisticsCardItem
        key="state-statistics-audience-age"
        heading={__t('state_statistics.audience.age.title')}
        tooltipMessage={__t('state_statistics.audience.age.tooltip')}
      >
        <PieChart
          id="audience-age"
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </StateStatisticsCardItem>
    );
  }

  renderInterests(): React.ReactNode {
    const { aggregateData } = this.props;
    const { audienceTopInterests } = aggregateData;

    if (isEmpty(audienceTopInterests)) {
      return null;
    }

    return (
      <StateStatisticsCardItem
        key="state-statistics-interests"
        heading={__t('state_statistics.audience.top_interests.title')}
        tooltipMessage={__t('state_statistics.audience.top_interests.tooltip')}
      >
        <div
          className="state-statistics__interests"
          data-qa="state-statistics__interests"
        >
          {
            audienceTopInterests.map((interest, index) => (
              <div
                key={`state-statistics__interest--${interest.label}`}
                className={`state-statistics__interest state-statistics__interest-${index + 1}`}
                data-qa={`state-statistics__interest--${interest.label}`}
              >
                <div className="state-statistics__interest-icon">
                  <SVGIcon
                    name={interest.label}
                    isBackgroundContain
                    isBackgroundCenter
                    width={20}
                    height={20}
                  />
                </div>
                <div
                  className="state-statistics__interest-name text-overflow-ellipsis"
                  title={__t(`collabary_common.specialized_in.${interest.label}`)}
                >
                  {__t(`collabary_common.specialized_in.${interest.label}`)}
                </div>
              </div>
            ))
          }
        </div>
      </StateStatisticsCardItem>
    );
  }

  renderAudienceCredibility(): React.ReactNode {
    const { aggregateData } = this.props;
    const { audienceCredibility } = aggregateData;

    return (
      <div className="state-statistics__credibility">
        <div className="state-statistics__credibility-label">
          {__t('state_statistics.audience.credibility.label')}:
        </div>
        <div className="state-statistics__credibility-value">
          {
            audienceCredibility ?
              __t(`state_statistics.audience.credibility.label_${audienceCredibility}`) :
              '-'
          }
        </div>
        <div className="state-statistics__credibility-tooltip">
          <Tooltip
            message={__t('state_statistics.audience.credibility.tooltip')}
            showQuestionMark
          />
        </div>
      </div>
    );
  }

  renderAudienceOverlap(): React.ReactNode {
    return (
      <StateStatisticsCardItem
        key="state-statistics-overlap"
        heading={__t('state_statistics.audience_overlap.overlap.title')}
        tooltipMessage={__t('state_statistics.audience_overlap.overlap.tooltip')}
      >
        <StateStatisticsOverlap />
      </StateStatisticsCardItem>
    );
  }

  renderCustomizableAudienceOverlap(): React.ReactNode {
    const { campaignId, offerState } = this.props;

    return (
      <StateStatisticsCardItem
        key="state-statistics-customizable-overlap"
        heading={__t('state_statistics.audience_overlap.customizable_overlap.title')}
        tooltipMessage={__t('state_statistics.audience_overlap.customizable_overlap.tooltip')}
      >
        <StateStatisticsCustomizableOverlap
          campaignId={campaignId}
          offerState={offerState}
        />
      </StateStatisticsCardItem>
    );
  }

  renderCreatorSection(): React.ReactNode {
    return (
      <div className="state-statistics__section">
        <h2 className="state-statistics__section-title">
          {__t('state_statistics.creator.title')}
        </h2>
        <div className="state-statistics__section-cards">
          <StateStatisticsCard>
            {this.renderFollowersChart()}
            <StateStatisticsCardItemDivider />
            {this.renderGenderChart('creatorGenderSplit', 'creator')}
          </StateStatisticsCard>
          <StateStatisticsCard>
            {this.renderTopCountriesChart('creator', 'creatorTopCountries', 'creator')}
            <StateStatisticsCardItemDivider />
            {this.renderBrandAffinity()}
          </StateStatisticsCard>
        </div>
      </div>
    );
  }

  renderAudienceSection(): React.ReactNode {
    const { channel } = this.props;
    const isInstagramChannel = channel === 'instagram';

    return (
      <div className="state-statistics__section">
        <h2 className="state-statistics__section-title">
          {__t('state_statistics.audience.title')}
        </h2>
        <div className="state-statistics__section-cards">
          {this.renderAudienceCredibility()}
          <StateStatisticsCard>
            {this.renderAgeChart()}
            <StateStatisticsCardItemDivider />
            {this.renderGenderChart('audienceGenderSplit', 'audience')}
          </StateStatisticsCard>
          <StateStatisticsCard
            type={isInstagramChannel ? 'double' : 'single'}
          >
            {this.renderTopCountriesChart('audience', 'audienceTopCountries', 'audience', true)}
            {
              isInstagramChannel ?
                <React.Fragment>
                  <StateStatisticsCardItemDivider />
                  {this.renderInterests()}
                </React.Fragment> :
                null
            }
          </StateStatisticsCard>
        </div>
      </div>
    );
  }

  renderAudienceOverlapSection(): React.ReactNode {
    const { campaignId, offerState } = this.props;
    const { channel } = this.props;

    if (channel !== 'instagram') {
      return null;
    }

    return (
      <div className="state-statistics__section">
        <h2 className="state-statistics__section-title">
          {__t('state_statistics.audience_overlap.title')}
        </h2>
        <div className="state-statistics__section-cards">
          <StateStatisticsOverlapStatusChecker
            campaignId={campaignId}
            offerState={offerState}
          >
            <React.Fragment>
              <StateStatisticsCard type="single">
                {this.renderAudienceOverlap()}
              </StateStatisticsCard>
              <StateStatisticsCard type="single">
                {this.renderCustomizableAudienceOverlap()}
              </StateStatisticsCard>
            </React.Fragment>
          </StateStatisticsOverlapStatusChecker>
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { aggregateData } = this.props;
    const { isLoading } = aggregateData;

    return (
      <section className="state-statistics">
        {this.renderChannelRadioButtons()}
        {
          isLoading ?
            <div className="state-statistics__spinner">
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div> :
            <div className="state-statistics__container">
              {this.renderCreatorSection()}
              {this.renderAudienceSection()}
              {this.renderAudienceOverlapSection()}
            </div>
        }
      </section>
    );
  }
}

const mapStateToProps = ({ subcampaignStatistics }): StateProps => ({
  aggregateData: subcampaignStatistics
});

const mapDispatchToProps = (
  dispatch,
  {
    campaignId,
    offerState
  }: OwnProps
): Dispatchprops => ({
  fetchStats: (channel) => dispatch(fetchStats(campaignId, offerState, channel)),
  dispatchResetAggregates: () => dispatch(resetAggregates())
});

export default connect(mapStateToProps, mapDispatchToProps)(StateStatistics);
